import {
  SHRINE_COMMON_COST_AMOUNT,
  SHRINE_COMMON_COST_KINDS,
  SHRINE_RARE_COST_AMOUNT,
  SHRINE_RARE_COST_KINDS,
  SHRINE_MYTHIC_COST_KIND,
  SHRINE_MYTHIC_COST_AMOUNT,
} from './shrineConfig';

// Small deterministic RNG (mulberry32)
function mulberry32(seed) {
  let t = seed >>> 0;
  return function rand() {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function shrineSeed(runSeed, floorLevel, x, y) {
  // A stable, coordinate-specific 32-bit seed.
  // Constants are just good mix factors.
  let s = (runSeed >>> 0) ^ ((floorLevel * 0x9e3779b9) >>> 0);
  s ^= Math.imul((x + 11) >>> 0, 0x85ebca6b);
  s ^= Math.imul((y + 23) >>> 0, 0xc2b2ae35);
  return s >>> 0;
}

export function getShrineTierFromTile(tile) {
  if (tile === 'shrine-common') return 'common';
  if (tile === 'shrine-rare') return 'rare';
  if (tile === 'shrine-mythic') return 'mythic';
  return null;
}

/**
 * Build a per-floor dictionary of shrine metadata keyed by "x,y".
 * Tier is read from the map tile. Cost is derived deterministically from runSeed/floor/x/y.
 */
export function initShrinesForMap(map, runSeed, floorLevel) {
  const out = {};
  if (!Array.isArray(map) || map.length === 0) return out;

  const h = map.length;
  const w = map[0]?.length ?? 0;

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const tile = map[y]?.[x];
      if (typeof tile !== 'string' || !tile.startsWith('shrine-')) continue;

      const tier = getShrineTierFromTile(tile);
      if (!tier) continue;

      const rng = mulberry32(shrineSeed(runSeed, floorLevel, x, y));

      let cost = null;
      if (tier === 'common') {
        const idx = Math.floor(rng() * SHRINE_COMMON_COST_KINDS.length);
        const kind = SHRINE_COMMON_COST_KINDS[idx] || SHRINE_COMMON_COST_KINDS[0];
        cost = { kind, amount: SHRINE_COMMON_COST_AMOUNT };
      } else if (tier === 'rare') {
        const idx = Math.floor(rng() * SHRINE_RARE_COST_KINDS.length);
        const kind = SHRINE_RARE_COST_KINDS[idx] || SHRINE_RARE_COST_KINDS[0];
        cost = { kind, amount: SHRINE_RARE_COST_AMOUNT };
      } else {
        cost = { kind: SHRINE_MYTHIC_COST_KIND, amount: SHRINE_MYTHIC_COST_AMOUNT };
      }

      out[`${x},${y}`] = {
        tier,
        cost,
      };
    }
  }

  return out;
}

export function formatShrineTier(tier) {
  if (tier === 'rare') return 'Rare';
  if (tier === 'mythic') return 'Mythic';
  return 'Common';
}

export function formatShrineCost(cost) {
  if (!cost) return '';
  const amt = Number(cost.amount) || 0;
  switch (cost.kind) {
    case 'teeth':
      return `${amt} Tooth${amt === 1 ? '' : 's'}`;
    case 'key':
      return `${amt} Key${amt === 1 ? '' : 's'}`;
    case 'potion':
      return `${amt} Potion${amt === 1 ? '' : 's'}`;
    case 'torch':
      return `${amt} Torch${amt === 1 ? '' : 'es'}`;
    case 'maxHp':
      return `${amt} Max HP`;
    case 'maxSp':
      return `${amt} Max SP`;
    case 'deckCard':
      return `${amt} Card from your deck`;
    default:
      return String(cost.kind);
  }
}

export function canAffordShrineCost({ cost, resources, deckIds }) {
  if (!cost) return false;
  const amt = Math.max(0, Math.floor(Number(cost.amount) || 0));

  switch (cost.kind) {
    case 'teeth':
      return (Number(resources?.teeth) || 0) >= amt;
    case 'key':
      return (Number(resources?.keys) || 0) >= amt;
    case 'potion':
      return (Number(resources?.potions) || 0) >= amt;
    case 'torch':
      return (Number(resources?.torches) || 0) >= amt;
    case 'maxHp':
      return (Number(resources?.maxHp) || 0) - amt >= 1;
    case 'maxSp':
      return (Number(resources?.maxSp) || 0) - amt >= 1;
    case 'deckCard':
      return Array.isArray(deckIds) && deckIds.length >= amt;
    default:
      return false;
  }
}
